import React from "react";
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Typography,
    Chip,
    useMediaQuery,
    useTheme,
    Card,
    CardContent,
    Divider,
} from "@mui/material";
import Pagination from "@mui/material/Pagination";

const CommonTables = (props) => {
    const {
        tableHeads,
        tableData,
        perPage,
        currentPage,
        perPageChange,
        pageChange,
        totalCount,
    } = props;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const isSmall = useMediaQuery(theme.breakpoints.down('sm'));

    const totalPages = Math.ceil(totalCount / perPage);
    const startEntry = (currentPage - 1) * perPage + 1;
    const endEntry = Math.min(currentPage * perPage, totalCount);

    // Debug logging (remove in production)


    // Ensure we have valid data
    if (!tableData || !tableHeads || tableData.length === 0) {
        return (
            <Box sx={{
                width: "100%",
                textAlign: "center",
                py: 4,
                color: "#64748b"
            }}>
                <Typography variant="h6">No data available</Typography>
            </Box>
        );
    }



    return (
        <Box sx={{ width: "100%" }}>
            {/* Desktop/Tablet Table View */}

            <TableContainer
                component={Paper}
                sx={{
                    borderRadius: 3,
                    overflow: "hidden",
                    boxShadow: "0 8px 32px rgba(0, 0, 0, 0.08)",
                    border: "1px solid",
                    borderColor: "grey.200",
                    background: "linear-gradient(145deg, #ffffff 0%, #fafafa 100%)",
                    // Enable horizontal scrolling on smaller screens
                    overflowX: "auto",
                    "&::-webkit-scrollbar": {
                        height: 8,
                    },
                    "&::-webkit-scrollbar-track": {
                        backgroundColor: "#f1f1f1",
                    },
                    "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "#c1c1c1",
                        borderRadius: 4,
                        "&:hover": {
                            backgroundColor: "#a8a8a8",
                        },
                    },
                }}
            >
                <Table
                    sx={{
                        minWidth: 650,
                        // Ensure table maintains minimum width for readability
                        width: "100%",
                    }}
                    aria-label="responsive data table"
                >
                    {/* Enhanced Table Head */}
                    <TableHead>
                        <TableRow
                            sx={{
                                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                                position: "relative",
                                "&::after": {
                                    content: '""',
                                    position: "absolute",
                                    bottom: 0,
                                    left: 0,
                                    right: 0,
                                    height: "2px",
                                    background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)",
                                },
                            }}
                        >
                            {tableHeads.map((head, index) => (
                                <TableCell
                                    key={`header-${index}`}
                                    align="center"
                                    sx={{
                                        color: "white",
                                        fontWeight: 700,
                                        fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                        textTransform: "uppercase",
                                        letterSpacing: "0.1em",
                                        border: "none",
                                        py: { xs: 2, sm: 2.5 },
                                        px: { xs: 1.5, sm: 3 },
                                        textShadow: "0 1px 2px rgba(0,0,0,0.2)",
                                        position: "relative",
                                        minWidth: { xs: "80px", sm: "120px" },
                                        whiteSpace: "nowrap",
                                        "&:not(:last-child)::after": {
                                            content: '""',
                                            position: "absolute",
                                            right: 0,
                                            top: "20%",
                                            height: "60%",
                                            width: "1px",
                                            background: "rgba(255,255,255,0.2)",
                                        },
                                    }}
                                >
                                    {head}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    {/* Enhanced Table Body */}
                    <TableBody>
                        {tableData.map((row, rowIndex) => (
                            <TableRow
                                key={row.MemberId || `row-${rowIndex}`}
                                sx={{
                                    backgroundColor: rowIndex % 2 === 0 ? "#fafafa" : "#ffffff",
                                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                    position: "relative",
                                    "&:hover": {
                                        backgroundColor: "#f0f7ff",
                                        boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
                                        "& td": {
                                            borderColor: "#e3f2fd",
                                        },
                                    },
                                    "&:last-child td": {
                                        borderBottom: "none",
                                    },
                                }}
                            >
                                {row.map((data, colIndex) => {
                                    // Handle different data types and null/undefined values


                                    return (
                                        <TableCell
                                            key={`${row.MemberId || rowIndex}-${colIndex}`}
                                            align="center"
                                            sx={{
                                                fontSize: { xs: "0.8rem", sm: "0.95rem" },
                                                fontFamily: "'Inter', 'Segoe UI', 'Roboto', sans-serif",
                                                color: "#2c3e50",
                                                fontWeight: 500,
                                                borderBottom: "1px solid #e8eaed",
                                                py: { xs: 1.5, sm: 2 },
                                                px: { xs: 1.5, sm: 3 },
                                                lineHeight: 1.5,
                                                transition: "all 0.2s ease",
                                                position: "relative",
                                                minWidth: { xs: "80px", sm: "120px" },
                                                maxWidth: { xs: "120px", sm: "250px" },
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    display: "inline-block",
                                                    width: "100%",
                                                    overflow: "hidden",
                                                    textOverflow: "ellipsis",
                                                    whiteSpace: "nowrap",
                                                    wordBreak: "break-all",
                                                }}
                                            //title={data} // Tooltip for full text
                                            >
                                                {data}
                                            </Box>
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>


            {/* Responsive Footer with Enhanced Design */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    justifyContent: "space-between",
                    alignItems: { xs: "stretch", md: "center" },
                    mt: 3,
                    p: { xs: 2, sm: 3 },
                    backgroundColor: "#f8fafc",
                    borderRadius: 2,
                    border: "1px solid #e2e8f0",
                    gap: 2,
                }}
            >
                {/* Left Section: Entries Info */}
                <Box
                    sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                        alignItems: { xs: "stretch", sm: "center" },
                        gap: 2,
                        width: { xs: "100%", md: "auto" },
                    }}
                >
                    {/* Entries Selector */}
                    <FormControl
                        variant="outlined"
                        size="small"
                        sx={{
                            minWidth: { xs: "100%", sm: 140 },
                            maxWidth: { xs: "100%", sm: 200 },
                            "& .MuiOutlinedInput-root": {
                                borderRadius: 2,
                                backgroundColor: "white",
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#667eea",
                                    borderWidth: "2px",
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                                    borderColor: "#667eea",
                                    borderWidth: "2px",
                                },
                            },
                        }}
                    >
                        <InputLabel
                            id="per-page-label"
                            sx={{
                                color: "#64748b",
                                "&.Mui-focused": { color: "#667eea" },
                            }}
                        >
                            {isSmall ? "Entries" : "Show entries"}
                        </InputLabel>
                        <Select
                            labelId="per-page-label"
                            id="per-page-select"
                            value={perPage}
                            label={isSmall ? "Entries" : "Show entries"}
                            onChange={perPageChange}
                        >
                            {[10, 15, 20, 25, 50, 75, 100, 500, 1000].map((num) => (
                                <MenuItem
                                    key={num}
                                    value={num}
                                    sx={{
                                        "&:hover": {
                                            backgroundColor: "#f1f5f9",
                                        },
                                    }}
                                >
                                    {num}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {/* Enhanced Records Info */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: { xs: "center", sm: "flex-start" },
                            gap: 1,
                            flexWrap: "wrap",
                            mt: { xs: 1, sm: 0 },
                        }}
                    >
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#64748b",
                                fontWeight: 500,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                        >
                            {isSmall ? "" : "Showing"}
                        </Typography>
                        <Chip
                            label={`${startEntry}-${endEntry}`}
                            size="small"
                            sx={{
                                backgroundColor: "#e0f2fe",
                                color: "#0277bd",
                                fontWeight: 600,
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                height: { xs: 24, sm: 28 },
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#64748b",
                                fontWeight: 500,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                        >
                            of
                        </Typography>
                        <Chip
                            label={totalCount.toLocaleString()}
                            size="small"
                            sx={{
                                backgroundColor: "#f3e5f5",
                                color: "#7b1fa2",
                                fontWeight: 600,
                                fontSize: { xs: "0.7rem", sm: "0.75rem" },
                                height: { xs: 24, sm: 28 },
                            }}
                        />
                        <Typography
                            variant="body2"
                            sx={{
                                color: "#64748b",
                                fontWeight: 500,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                            }}
                        >
                            {isSmall ? "" : "entries"}
                        </Typography>
                    </Box>
                </Box>

                {/* Right Section: Enhanced Pagination */}
                <Box
                    sx={{
                        display: "flex",
                        justifyContent: { xs: "center", md: "flex-end" },
                        mt: { xs: 2, md: 0 },
                    }}
                >
                    <Pagination
                        count={totalPages}
                        page={currentPage}
                        onChange={pageChange}
                        variant="outlined"
                        shape="rounded"
                        color="primary"
                        showFirstButton={!isSmall}
                        showLastButton={!isSmall}
                        size={isSmall ? "small" : "medium"}
                        siblingCount={isSmall ? 0 : 1}
                        boundaryCount={isSmall ? 1 : 2}
                        sx={{
                            "& .MuiPaginationItem-root": {
                                borderColor: "#e2e8f0",
                                color: "#64748b",
                                fontWeight: 500,
                                fontSize: { xs: "0.75rem", sm: "0.875rem" },
                                minWidth: { xs: 28, sm: 32 },
                                height: { xs: 28, sm: 32 },
                                "&:hover": {
                                    borderColor: "#667eea",
                                    backgroundColor: "#f1f5f9",
                                },
                                "&.Mui-selected": {
                                    backgroundColor: "#667eea",
                                    color: "white",
                                    borderColor: "#667eea",
                                    "&:hover": {
                                        backgroundColor: "#5a6fd8",
                                    },
                                },
                            },
                            "& .MuiPaginationItem-previousNext": {
                                color: "#667eea",
                                "&:hover": {
                                    backgroundColor: "#f1f5f9",
                                },
                            },
                        }}
                    />
                </Box>
            </Box>
        </Box>
    );
};

export default CommonTables;